import { Box, createStyles, Group, Space, Stack } from '@mantine/core';

import type { AIUpdate, LiteSecodaEntity } from '@repo/api-codegen';
import { Text } from '@repo/foundations';
import { compact, isNil, map, reverse } from 'lodash-es';
import type { IApiListResponse, INestedBreadcrumbs } from '../../../api';
import { useIntegrationList } from '../../../api';
import { useNestedBreadcrumbsList } from '../../../api/hooks/nestedBreadcrumbs';
import { getBreadcrumbForEntity } from '../../../utils/breadcrumb';
import { SecodaEntityIcon } from '../../SecodaEntity/SecodaEntityIcon';

const useStyles = createStyles((theme) => ({
	wrapper: {
		padding: theme.spacing.xs,
		borderRadius: theme.radius.sm,
		backgroundColor: theme.other.getColor('surface/primary/default'),
		'&:hover': {
			backgroundColor: theme.other.getColor('fill/transparent/hover'),
		},
		'&:active': {
			backgroundColor: theme.other.getColor('surface/primary/active'),
		},
	},
	icon: {
		flexShrink: 0,
		color: theme.other.getColor('icon/primary/default'),
	},
	rightIcon: {
		color: theme.other.getColor('icon/secondary/default'),
	},
	grow: {
		flexGrow: 1,
	},
	title: {
		color: theme.other.getColor('text/primary/default'),
		fontWeight: theme.other.typography.weight.semibold,
		fontSize: theme.other.typography.text.sm,
		lineHeight: theme.other.typography.lineHeight.text.sm,
	},
	breadcrumb: {
		color: theme.other.getColor('text/secondary/default'),
		fontSize: theme.other.typography.text.xs,
		lineHeight: theme.other.typography.lineHeight.text.xs,
	},
}));

export function AutomationEntityRender({
	entity,
}: {
	entity: LiteSecodaEntity;
}): JSX.Element {
	const entityId = entity?.id;
	const { classes } = useStyles();

	const { data: integrations } = useIntegrationList({
		options: {
			select: (res) => res.results,
		},
	});

	const { data: nestedBreadcrumbs } = useNestedBreadcrumbsList({
		filters: {
			id: entityId!,
		},
		options: {
			enabled: !isNil(entityId),
			select: (res: IApiListResponse<INestedBreadcrumbs>) =>
				reverse(res.results || []),
		},
	});

	const title = entity?.title;
	const breadcrumb = getBreadcrumbForEntity({
		entity,
		nativeType: '',
		team: null,
		nestedBreadcrumbs,
		integrations,
		includeTeam: false,
	});

	const breadcrumbText = compact(map(breadcrumb, 'title')).join(' / ');

	return (
		<Group position="apart" noWrap>
			<Group spacing="xs" noWrap align="flex-start">
				<Box className={classes.icon}>
					<SecodaEntityIcon entity={entity} size={20} />
				</Box>
				<Stack spacing={4} className={classes.grow}>
					<Text className={classes.title} lineClamp={1}>
						{title ?? 'Untitled'}
					</Text>
					{breadcrumbText ? (
						<Text className={classes.breadcrumb} lineClamp={1}>
							{breadcrumbText}
						</Text>
					) : (
						<Space h="xs" />
					)}
				</Stack>
			</Group>
		</Group>
	);
}

export function AutomationEntityAiOutputRender({
	aiUpdate,
}: {
	aiUpdate: AIUpdate;
}): JSX.Element {
	const generatedAttributes = [];
	if (aiUpdate.description) {
		generatedAttributes.push('description');
	}

	if (aiUpdate.definition) {
		generatedAttributes.push('definition');
	}

	if (aiUpdate.owners) {
		generatedAttributes.push('owners');
	}

	if (aiUpdate.tags) {
		generatedAttributes.push('tags');
	}

	if (!isNil(aiUpdate.pii)) {
		generatedAttributes.push('pii');
	}

	if (generatedAttributes.length === 0) {
		return <Text size="sm">No AI output</Text>;
	}

	return <Text size="sm">{generatedAttributes.join(', ')}</Text>;
}
